import { useState, useEffect, useMemo, useCallback } from 'react';
import { generatePath, useParams, Outlet } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  useQueryState,
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  Checkbox,
  ButtonPseudo,
  SidePanelLoader,
  InputText,
  IconSearch,
  PermissionEnum,
  Currency,
  formatBalance,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  selectFees,
  getLogs,
  setTariffSearchValue,
} from '../store/slices/tariffs/tariff';
import CustomerFees from '../../../components/partial/tariff/fees';
// import CustomerTariffsLogs from '../../../components/partial/tariff/logs';
import { feesSchema } from '../variables/fees';
import {
  useLazyGetCustomersTariffsListQuery,
  useAssignTariffToCustomerMutation,
  useDeleteCustomFeeMutation,
  useLazyGetCustomersRateSourcesQuery,
} from '../store/services/customers';
import tariffSelector from '../store/slices/tariffs/tariffs-selector';
import { CustomerType, FeeItem, FeeAmountTypeEnum } from '../types';
import { GeneralInfo } from '../../individual/types';
import { CorporateGeneralInfo } from '../../corporate/types';
import CustomMargins from '../../../components/partial/tariff/margins';
import MarginDetails from '../../../components/partial/tariff/margin-details';

import ROUTES from '../../../routes';

import cn from '../../../components/partial/tariff/tariffs.module.css';

const formatFeesList = (
  list: FeeItem[],
  customerId?: string,
  customerType?: CustomerType,
  baseCurrency?: Currency,
  currenciesList?: Currency[],
): FeeItem[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const currentCurrency = result?.feeCurrencyId
      ? currenciesList?.find(
          (currency) => currency.id === result?.feeCurrencyId,
        )
      : baseCurrency;

    const currentCurrencyName = currentCurrency?.name;

    result.url = generatePath(
      customerType === CustomerType.INDIVIDUAL
        ? ROUTES.CUSTOMERS_INDIVIDUAL_TARIFFS_FEE
        : ROUTES.CUSTOMERS_CORPORATE_TARIFFS_FEE,
      {
        customerId: customerId as string,
        feeId: result.id,
      },
    );

    const render: Record<string, React.JSX.Element> = {
      name: (
        <div className={cn.feeName}>
          {item.name}
          {item.isModified && <div className={cn.modified}>Modified</div>}
        </div>
      ),
      feeAmountType: (
        <div>
          {result.feeAmountType === FeeAmountTypeEnum.FIXED
            ? 'Fixed value'
            : 'Percent of balance'}
        </div>
      ),
      currency: <div>{currentCurrencyName}</div>,
      writeOffValue: (
        <div>
          {result.feeAmountType === FeeAmountTypeEnum.PERCENT
            ? `${result.feePercentAmount}% ${
                result.feePercentMinAmount || result.feePercentMaxAmount
                  ? `(${
                      result.feePercentMinAmount
                        ? `min: ${formatBalance(
                            result.feePercentMinAmount,
                            baseCurrency,
                          )} ${baseCurrency?.name}`
                        : ''
                    }${
                      result.feePercentMaxAmount
                        ? `${
                            result.feePercentMinAmount ? `, ` : ''
                          }max: ${formatBalance(
                            result.feePercentMaxAmount,
                            baseCurrency,
                          )} ${baseCurrency?.name}`
                        : ''
                    })`
                  : ''
              }`
            : `${formatBalance(
                result.feeFixedAmount || 0,
                currentCurrency,
              )} ${currentCurrencyName}`}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const CustomerTariffs = ({ customerType }: { customerType: CustomerType }) => {
  const [selectedTariffId, setSelectedTariffId] = useState('');
  const [allMarginsVisible, setAllMarginsVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [showMoreCurrencies, setShowMoreCurrencies] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [menuOnFocus, setMenuOnFocus] = useState(false);
  const [selectedMarginId, setSeletedMarginId] = useState('');
  const [marginPanelIsOpen, setMarginPanelIsOpen] = useState(false);
  const {
    customerTariffs: {
      fees,
      logs,
      riskScoreRanges,
      searchValue,
      customMargins,
      rateSources,
      customMarginsPagination,
      defaultMargin,
    },
    individualCustomers: {
      info: {
        general: { baseInformation, baseInformationIsLoading },
      },
    },
    corporateCustomers: {
      info: {
        general: {
          baseInformation: corporateGeneralInfo,
          baseInformationIsLoading: corporateBaseInformationIsLoading,
        },
      },
    },
    customersDictionaries: {
      activePermissions,
      riskScoreRanges: riskScoreRangesDictionaries,
      baseCurrency,
      currencies,
      currenciesList,
    },
  } = useAppSelector((state) => state);

  const tariffInfo: Partial<GeneralInfo> | Partial<CorporateGeneralInfo> =
    useMemo(() => {
      if (customerType === CustomerType.INDIVIDUAL) {
        return baseInformation as Partial<GeneralInfo>;
      }
      return corporateGeneralInfo as Partial<CorporateGeneralInfo>;
    }, [baseInformation, corporateGeneralInfo]);

  const riskLevel = useMemo(
    () =>
      riskScoreRangesDictionaries?.find(
        (item) => tariffInfo?.tariffRiskScoreId === item.id,
      )?.name,
    [tariffInfo?.risk, riskScoreRangesDictionaries],
  );

  const { tariffsList } = useAppSelector(tariffSelector);

  const dispatch = useAppDispatch();

  const { customerId } = useParams();

  const { sort } = useQueryState(feesSchema);

  const [getRateSource] = useLazyGetCustomersRateSourcesQuery();

  const [
    assignTariffToCustomer,
    { isLoading: tariffChangeInProgress, isSuccess: tariffChangedSuccess },
  ] = useAssignTariffToCustomerMutation();
  const [
    deleteFee,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useDeleteCustomFeeMutation();
  const [getTariffsList, { isLoading }] = useLazyGetCustomersTariffsListQuery();

  const onDelete = () => {
    const customFees: string[] = [];

    fees.selectedFees.forEach((item) => {
      const currentFee = tariffInfo?.fees?.find((fee) => fee.id === item);

      if (currentFee && currentFee.isModified) {
        customFees.push(currentFee.id);
      }
    });

    customFees.forEach((item) => {
      deleteFee({
        id: customerId as string,
        feeId: item,
      });
    });
  };

  const assignTariffToCustomerAction = () => {
    assignTariffToCustomer({
      body: { tariffId: selectedTariffId },
      id: customerId as string,
    });
  };

  const getTariffsListAction = useCallback(() => {
    getTariffsList(
      `&type=${
        customerType === CustomerType.INDIVIDUAL ? 'INDIVIDUAL' : 'CORPORATE'
      }`,
    );
  }, []);

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(rows, fees.selectedFees)) {
      dispatch(selectFees(rows));
    }
  };

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
  }, []);

  useEffect(() => {
    if (!tariffsList) {
      getTariffsListAction();
    }
  }, [getTariffsListAction, tariffsList]);

  useEffect(() => {
    dispatch(getLogs());
  }, [dispatch, logs.values]);

  useEffect(() => {
    if (tariffChangedSuccess) {
      handleClose();
    }
  }, [tariffChangedSuccess]);

  useEffect(() => {
    let title: string;

    const companyName = localStorage.getItem('companyName');

    if (customerType === CustomerType.INDIVIDUAL) {
      if (baseInformation?.firstName || baseInformation?.lastName) {
        title = `${baseInformation?.firstName} ${baseInformation?.lastName}: Tariff`;
      } else {
        title = 'Tariff';
      }
    } else if (corporateGeneralInfo?.name) {
      title = `${corporateGeneralInfo?.name}: Tariff`;
    } else {
      title = 'Tariff';
    }

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [
    baseInformation?.firstName,
    baseInformation?.lastName,
    corporateGeneralInfo?.name,
  ]);

  useEffect(() => {
    if (!rateSources) {
      getRateSource();
    }
  }, [rateSources]);

  const formattedData = useMemo(() => {
    const data = formatFeesList(
      tariffInfo?.fees as FeeItem[],
      customerId,
      customerType,
      baseCurrency,
      currenciesList,
    );

    return data;
  }, [tariffInfo?.fees, baseCurrency, currenciesList]);

  let tariffsContent;

  if (isLoading) {
    tariffsContent = <SidePanelLoader />;
  } else {
    tariffsContent = (
      <div>
        {tariffsList
          ?.filter((item) => item.id !== tariffInfo?.tariffId)
          ?.map((item) => (
            <div className={cn.tariffRadioWrapper} key={item.id}>
              <div className={cn.tariffRadioValue}>
                <Checkbox
                  name={`radio-${item.id}`}
                  title={item.name}
                  type="radio"
                  onChange={() => setSelectedTariffId(item.id)}
                  checked={Boolean(selectedTariffId === item.id)}
                  visualClassName={cn.tariffRadioName}
                />
              </div>
              <div className={cn.tariffRadioValue}>
                {
                  riskScoreRanges.find(
                    (range) => range.value === item.riskScoreRangeId,
                  )?.name
                }
              </div>
            </div>
          ))}
      </div>
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title={`Change tariff to ${
          customerType === CustomerType.INDIVIDUAL
            ? `${baseInformation?.firstName} ${baseInformation?.lastName}`
            : corporateGeneralInfo?.name
        }`}
        sidepanelExpandedKey="customers-change-tariff"
        footer={
          <>
            <ButtonPrimary
              text={tariffChangeInProgress ? 'Loading...' : 'Apply'}
              onClick={() => assignTariffToCustomerAction()}
              disabled={
                Boolean(
                  (tariffInfo?.tariffId as string) === selectedTariffId,
                ) || !selectedTariffId
              }
              bold
            />
            <ButtonPseudo text="Close" onClick={handleClose} bold />
          </>
        }
      >
        <>
          <div className={cn.tariffSearchWrapper}>
            <InputText
              name="searchValue"
              value={searchValue}
              placeholder="Search tariff..."
              prefix={<IconSearch />}
              onChange={(e) => dispatch(setTariffSearchValue(e.target.value))}
              prefixClassName={cn.tariffSearchPrefix}
              className={cn.tariffSearch}
            />
          </div>
          <div className={cn.tariffHeader}>
            <div className={cn.tariffValue}>Tariff</div>
            <div className={cn.tariffValue}>Risk score</div>
          </div>
          {tariffsContent}
        </>
      </SidePanel>
      {!allMarginsVisible && (
        <CustomerFees
          isLoading={
            customerType === CustomerType.INDIVIDUAL
              ? (baseInformationIsLoading as boolean)
              : (corporateBaseInformationIsLoading as boolean)
          }
          values={formattedData}
          sort={sort}
          onRowsSelect={onRowsSelect}
          tariff={tariffInfo?.tariff}
          tariffRiskLevel={
            riskLevel
              ? riskLevel[0] + riskLevel.slice(1).toLowerCase()
              : undefined
          }
          tariffId={tariffInfo?.tariffId}
          deleteInProgress={deleteInProgress}
          selectedFees={fees.selectedFees}
          isEditable={activePermissions?.includes(
            customerType === CustomerType.INDIVIDUAL
              ? PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_TARIFF_ACTION
              : PermissionEnum.CORPORATE_CUSTOMERS_EDIT_TARIFF_ACTION,
          )}
          onDeleteFees={() => onDelete()}
          deleteSuccess={deleteIsSuccess}
          showSidepanel={() => setPanelIsOpen(true)}
          status={
            customerType === CustomerType.INDIVIDUAL
              ? baseInformation?.status
              : corporateGeneralInfo?.status
          }
        />
      )}

      {/* <CustomerTariffsLogs isLoading={logs.isLoading} logs={logs.values} /> */}
      <CustomMargins
        allMarginsVisible={allMarginsVisible}
        setAllMarginsVisible={setAllMarginsVisible}
        setSelectedCurrency={setSelectedCurrency}
        currencies={currencies}
        currenciesList={currenciesList}
        selectedCurrency={selectedCurrency}
        showMoreCurrencies={showMoreCurrencies}
        setShowMoreCurrencies={setShowMoreCurrencies}
        setMenuOnFocus={setMenuOnFocus}
        customMargins={customMargins}
        customerId={customerId}
        tariffId={tariffInfo?.tariffId}
        customMarginsPagination={customMarginsPagination}
        menuOnFocus={menuOnFocus}
        rateSources={rateSources}
        setSeletedMarginId={setSeletedMarginId}
        setMarginPanelIsOpen={setMarginPanelIsOpen}
        defaultMargin={defaultMargin}
      />
      <MarginDetails
        panelIsOpen={marginPanelIsOpen}
        setPanelIsOpen={setMarginPanelIsOpen}
        allMarginsVisible={allMarginsVisible}
        selectedCurrency={selectedCurrency}
        customerId={customerId}
        selectedMarginId={selectedMarginId}
        defaultMargin={defaultMargin}
        tariffId={tariffInfo?.tariffId}
        status={
          customerType === CustomerType.INDIVIDUAL
            ? baseInformation?.status
            : corporateGeneralInfo?.status
        }
      />
      <div className={cn.marginsSeparator} />
      <Outlet />
    </>
  );
};

export default CustomerTariffs;
